import { computed, unref } from 'vue'
import { hasCoords, toCoord } from '../shared/address'
import { ORDER_TYPE } from '../shared/constants'

// Client-side mirror of AddressFeature's radius gate — instant feedback only.
// The server re-checks before creating the order (the gate can't be bypassed
// by a raw REST call). Independent of autocomplete: works with coords from any
// source, fails open when origin/radius/coords are missing.

const EARTH_RADIUS_MI = 3958.7613

function haversineMi(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return EARTH_RADIUS_MI * 2 * Math.asin(Math.min(1, Math.sqrt(a)))
}

/**
 * @param {Ref<string>} orderType  reactive order type ('delivery' | ...)
 * @param {Ref<object>} address    reactive canonical address (with lat/lng)
 * @param {object} [root]          _EZCL.data; defaults to window
 */
export function useDeliveryZone(orderType, address, root) {
  const data = root || window?._EZCL?.data || {}
  const cfg = data?.delivery || {}

  // Radius is a property of the delivery order-type. Phase 1 resolves a single
  // value (admin setting first, Clover maxRadius fallback) but reads it per
  // service so the contract is already per-type ready.
  function radiusForType(type) {
    const perService = toCoord(data?.services?.[type]?.radiusMi)
    if (perService !== null && perService > 0) return perService
    const global = toCoord(cfg?.radiusMi)
    return global !== null && global > 0 ? global : null
  }

  // zoneStatus: 'n/a' | 'skipped' | 'unknown' | 'ok' | 'outside'
  const zoneStatus = computed(() => {
    const type = unref(orderType)
    if (type !== ORDER_TYPE.DELIVERY) return 'n/a'

    const radius = radiusForType(type)
    const oLat = toCoord(cfg?.origin?.lat)
    const oLng = toCoord(cfg?.origin?.lng)
    if (radius === null || oLat === null || oLng === null) return 'skipped'

    const addr = unref(address)
    if (!hasCoords(addr)) return 'unknown' // manual entry, no coords → fail open

    const distance = haversineMi(oLat, oLng, toCoord(addr.lat), toCoord(addr.lng))
    return distance <= radius ? 'ok' : 'outside'
  })

  // Only 'outside' should block the order; every other state is permissive.
  const blocksOrder = computed(() => zoneStatus.value === 'outside')

  return { zoneStatus, blocksOrder }
}
